import { useState } from 'react';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, SocialIcon } from '@change/ds-core-ui';
import { Radio, RadioGroup } from '@change/ds-forms';
import { FacebookRegularIcon as FacebookIcon, RedditIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { RecommendedGroupCard } from './components/RecommendedGroupCard';
import { COPIES } from './constants';
import type { RecommendedGroup, RecommendedGroupPickerProps } from './types';

function platformIcon(platform: RecommendedGroupPickerProps['platform']) {
	const iconComponent = platform === 'facebook' ? <FacebookIcon /> : <RedditIcon />;
	return (
		<SocialIcon aria-label={`${platform} icon`} variant={platform} className="m-0 h-8 w-8 p-0" icon={iconComponent} />
	);
}

export function RecommendedGroupPicker(props: RecommendedGroupPickerProps) {
	const { platform, recommendedGroups, isOpen, onClose, onGroupSelected } = props;

	const [selectedGroup, setSelectedGroup] = useState<RecommendedGroup | undefined>(recommendedGroups[0]);

	const copies = COPIES[platform];

	const handleChange = (value: string) => {
		const recommendedGroup = recommendedGroups.find((group) => group.url === value);
		setSelectedGroup(recommendedGroup as RecommendedGroup);
	};

	return (
		<Modal isOpen={isOpen} isDismissable data-testid="sharing-modal" size="large" onClose={onClose} className="z-30">
			<ModalHeader title={copies.title} subtitle={copies.description} leadingSlot={platformIcon(platform)} />
			<ModalBody className="pt-2">
				<div className="w-full p-0.5">
					<RadioGroup
						data-testid="recommended-group-radio-group"
						data-qa="recommended-group-radio"
						name="recommendedGroups"
						label="recommendedGroups"
						labelIsVisible={false}
						onChange={handleChange}
						value={selectedGroup?.url}
						className="flex w-full flex-col"
					>
						{recommendedGroups.map((recommendedGroup) => {
							const selected = recommendedGroup.url === selectedGroup?.url;
							const selectedClass = selected ? 'outline-stroke-selected' : '';
							// The Radio component from the DS is not very flexible, so we need to override its styles to match the design
							return (
								<Radio
									data-testid="recommended-group-radio"
									className={cn(
										'relative h-[70px] w-full cursor-pointer overflow-hidden rounded-medium outline outline-[2px] outline-offset-0 outline-stroke-weaker elevation-low',
										// Radio circle styles
										'before:absolute before:right-4 before:top-1/2 before:h-4 before:w-4 before:-translate-y-1/2 before:rounded-full before:border before:border-stroke-strong before:transition-all before:content-[""]',
										// Radio dot styles
										'after:absolute after:right-[19px] after:top-1/2 after:h-[10px] after:w-[10px] after:-translate-y-1/2 after:rounded-full after:transition-all after:content-[""]',
										// Selected and focus styles
										'before:data-[selected]:border-stroke-selected before:data-[selected]:bg-fill-inverse-strong',
										'after:data-[selected]:bg-fill-selected',
										'data-[focus-visible]:outline data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-stroke-selected',
										selectedClass,
									)}
									value={recommendedGroup.url}
									key={recommendedGroup.url}
								>
									<RecommendedGroupCard recommendedGroup={recommendedGroup} platform={platform} />
								</Radio>
							);
						})}
					</RadioGroup>
				</div>
			</ModalBody>
			<ModalFooter>
				<Button
					data-testid="recommended-group-submit-button"
					data-qa="recommended-group-radio-button"
					disabled={!selectedGroup}
					onClick={() => {
						onGroupSelected(selectedGroup);
					}}
					variant="primary"
					size="small"
					aria-label="Select and review your post"
				>
					Select & review your post
				</Button>
			</ModalFooter>
		</Modal>
	);
}
